import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, Navigate } from 'react-router-dom';
import StaffDashboard from './pages/StaffDashboard';
import StaffRoomControl from './pages/StaffRoomControl';
import FamilyView from './pages/FamilyView';
import Login from './pages/Login';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import HospitalHome from './pages/HospitalHome';
import HospitalStaffLogin from './pages/HospitalStaffLogin';
import * as storageService from './services/storageService';
import { Activity, HeartPulse, Search, Shield, Clock, Zap, ArrowRight, Lock, Building, BellOff, Users, ShieldCheck, Loader2, LineChart, Megaphone, UserCog, Stethoscope, Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { FeaturesIsometric } from './components/FeaturesIsometric';
import { FeatureTerminal } from './components/FeatureTerminal';
import LandingNavbar from './components/LandingNavbar';

import HospitalRoleSelection from './pages/HospitalRoleSelection';
import PublicBoard from './pages/PublicBoard';
import HospitalDashboard from './pages/HospitalDashboard';
import HospitalAdminDashboard from './pages/HospitalAdminDashboard';
import AdminHub from './pages/staff/AdminHub';
import AdminPublicidad from './pages/staff/AdminPublicidad';
import AdminAccesos from './pages/staff/AdminAccesos';
import AdminIntegraciones from './pages/staff/AdminIntegraciones';
import LogoutSuccess from './pages/LogoutSuccess';
import GroupAdminDashboard from './pages/admin/GroupAdminDashboard';
import DevPortal from './pages/DevPortal';
import Contacto from './pages/Contacto';
import { AdminHQLogin } from './pages/admin/AdminHQLogin';
import SupervisionBanner from './components/SupervisionBanner';
import { useVersionPoller } from './hooks/useVersionPoller';

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  if (!storageService.isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
};

const Home = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [isDemoActive, setIsDemoActive] = useState(true);
  const [demoPhase, setDemoPhase] = useState<'typing' | 'pressing' | 'loading' | 'result'>('typing');
  const [demoText, setDemoText] = useState('');
  const [tvPhase, setTvPhase] = useState(0);


  // TV Mockup animation timeline
  useEffect(() => {
    const interval = setInterval(() => {
      setTvPhase(prev => (prev + 1) % 2);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  // Main timeline interval for the phone mockup
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 5);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Redirect staff to dashboard if already authenticated
  useEffect(() => {
    if (storageService.isAuthenticated()) {
      navigate('/staff'); // /staff routes to RoleSelection if logged in, which handles the dashboard routing natively preserving hospital scope
    }
  }, [navigate]);

  // Auto-type demo logic for the search bar
  useEffect(() => {
    if (!isDemoActive) return;

    let timeout: NodeJS.Timeout;
    const fullCode = "MT001";

    const runDemo = async () => {
      // 1. Reset
      setDemoPhase('typing');
      setDemoText('');

      // 2. Typing animation
      for (let i = 0; i <= fullCode.length; i++) {
        setDemoText(fullCode.slice(0, i));
        await new Promise(r => setTimeout(r, 200));
      }

      await new Promise(r => setTimeout(r, 600));

      // 3. Pressing
      setDemoPhase('pressing');
      await new Promise(r => setTimeout(r, 500));

      // 4. Loading
      setDemoPhase('loading');
      await new Promise(r => setTimeout(r, 2000));

      // 5. Result
      setDemoPhase('result');
      await new Promise(r => setTimeout(r, 3000));

      // 6. Loop
      if (isDemoActive) runDemo();
    };

    runDemo();

    return () => clearTimeout(timeout);
  }, [isDemoActive]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setIsDemoActive(false);
    if (code.trim()) {
      navigate('/family', { state: { prefillCode: code } });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsDemoActive(false);
    setCode(e.target.value.toUpperCase());
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans selection:bg-med-accent selection:text-white bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] animate-background-pan bg-[length:200%_200%] from-med-light/40 via-med-bg to-med-bg">

      {/* ── Floating Navbar ─────────────────────────────────────────────── */}
      <LandingNavbar />

      {/* Hero Section */}
      <section className="pt-44 pb-28 px-6 relative overflow-hidden flex flex-col items-center justify-center min-h-[92vh]">
        {/* Animated Background Blobs */}
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-med-accent/15 rounded-full blur-[120px] mix-blend-multiply opacity-60 animate-blob-drift-1"></div>
        <div className="absolute top-1/3 right-1/4 w-[450px] h-[450px] bg-indigo-400/15 rounded-full blur-[120px] mix-blend-multiply opacity-60 animate-blob-drift-2"></div>
        <div className="absolute -bottom-20 left-1/3 w-[600px] h-[600px] bg-med-light/30 rounded-full blur-[150px] mix-blend-multiply opacity-60 animate-blob-drift-3"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-emerald-100/10 rounded-full blur-[180px] mix-blend-multiply opacity-40 animate-blob-drift-4"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-white/80 backdrop-blur-md border border-med-light/50 shadow-sm mb-10 hover:shadow-md transition-shadow">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-med-success opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-med-success"></span>
            </span>
            <span className="text-[11px] font-medium text-med-navy/70 uppercase tracking-[0.08em] font-heading">Operando en hospitales ahora</span>
          </motion.div>

          {/* Headline — generous spacing */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-4xl md:text-6xl lg:text-7xl font-bold text-med-navy leading-[1.05] mb-8 tracking-[-0.04em] font-heading">
            La sala de espera ya no es <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-med-accent via-indigo-500 to-med-accent bg-[length:200%_auto] animate-[gradientShift_8s_linear_infinite]">un punto ciego.</span>
          </motion.h1>

          {/* Subtitle 1 */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-lg text-med-navy/70 max-w-2xl mx-auto mb-4 leading-[1.6] font-normal">
            Mientras tu equipo opera, las familias esperan sin información. <strong className="font-medium">Hospital Status cierra esa brecha en tiempo real.</strong>
          </motion.p>

          {/* Subtitle 2 */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-base text-med-navy/50 max-w-2xl mx-auto mb-16 leading-[1.6] font-normal">
            Visibilidad quirúrgica para familias. Control operativo para tu hospital. Sin instalar nada.
          </motion.p>

          {/* Premium Search Bar with Auto-Type Demo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="max-w-xl mx-auto relative mb-10 group">
            {/* Glow behind search */}
            <div className={`absolute -inset-6 bg-gradient-to-r from-med-accent/5 to-indigo-500/5 rounded-[48px] blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 -z-10`}></div>

            <div className={`bg-white/95 backdrop-blur-xl p-2.5 rounded-full shadow-[0_20px_40px_-15px_rgba(18,32,86,0.12)] border border-white/50 flex items-center relative z-20 hover:shadow-[0_25px_50px_-12px_rgba(91,101,220,0.25)] transition-all duration-500 ring-1 ring-med-light/30 focus-within:ring-med-accent/50 focus-within:scale-[1.02] ${isDemoActive && demoPhase === 'pressing' ? 'scale-[0.98]' : ''}`}>
              <div className="pl-6 pr-4 py-3 flex-1 text-left relative overflow-hidden">
                <label
                  htmlFor="code-input"
                  className={`block text-[11px] font-medium text-med-navy/50 uppercase tracking-[0.08em] mb-1.5 font-heading transition-opacity duration-500 ${isDemoActive && (demoPhase === 'loading' || demoPhase === 'result') ? 'opacity-0' : 'opacity-100'}`}
                >
                  Código del Paciente
                </label>
                <div className="relative flex items-center">
                  <input
                    id="code-input"
                    type="text"
                    value={isDemoActive ? demoText : code}
                    onChange={handleInputChange}
                    placeholder={isDemoActive ? "" : "Ej: MT001"}
                    autoComplete="off"
                    className={`w-full text-2xl font-bold text-med-navy placeholder:text-slate-300 focus:outline-none bg-transparent font-mono uppercase tracking-wider z-10 transition-opacity duration-300 ${isDemoActive && demoPhase === 'result' ? 'opacity-0' : 'opacity-100'}`}
                  />
                  {isDemoActive && demoPhase === 'typing' && (
                    <div
                      className="absolute w-0.5 h-7 bg-med-accent animate-cursor-blink pointer-events-none transition-all duration-100"
                      style={{
                        left: `${demoText.length * 16.8}px`,
                        transform: 'translateY(-2px)'
                      }}
                    ></div>
                  )}

                  {isDemoActive && demoPhase === 'loading' && (
                    <div className="absolute inset-x-0 -top-6 bottom-0 bg-white/95 backdrop-blur-md z-20 flex items-center gap-3 animate-fade-in">
                      <div className="flex gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-med-accent animate-bounce"></div>
                        <div className="w-2 h-2 rounded-full bg-med-accent animate-bounce [animation-delay:-0.15s]"></div>
                        <div className="w-2 h-2 rounded-full bg-med-accent animate-bounce [animation-delay:-0.3s]"></div>
                      </div>
                      <span className="text-[11px] font-medium text-med-navy/30 uppercase tracking-[0.08em]">Buscando...</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Result overlay — simplified & balanced */}
              {isDemoActive && demoPhase === 'result' && (
                <div className="absolute inset-0 bg-white z-30 flex items-center px-6 md:px-8 animate-scale-in rounded-full transition-all duration-500 shadow-md">
                  {/* Left: ID & Label */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-med-navy font-mono text-lg md:text-xl font-bold">MT001</span>
                    <span className="text-[10px] text-med-navy/50 font-medium uppercase tracking-[0.08em] px-2 py-1 bg-med-light/50 rounded-md">Artroscopía</span>
                  </div>

                  {/* Center: Divider + Progress */}
                  <div className="flex-1 mx-6 md:mx-8">
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="text-[10px] text-emerald-600 font-medium uppercase tracking-[0.08em]">En Cirugía</span>
                      <span className="text-[9px] md:text-[10px] text-med-navy/30 font-mono">75%</span>
                    </div>
                    <div className="h-1.5 w-full bg-med-light/50 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 w-3/4 rounded-full relative overflow-hidden">
                        <div className="absolute inset-0 bg-white/20 animate-[shimmer_2s_infinite]"></div>
                      </div>
                    </div>
                  </div>

                  {/* Right: Status badge */}
                  <div className="flex items-center gap-2 px-3 md:px-4 py-2 bg-emerald-50 rounded-full text-[11px] font-medium text-emerald-600 border border-emerald-100 shadow-sm whitespace-nowrap flex-shrink-0">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    Recuperación
                  </div>
                </div>
              )}
              <button
                onClick={handleSearch}
                className={`bg-gradient-to-r from-med-accent to-indigo-600 hover:from-indigo-600 hover:to-med-accent text-white p-4 h-[60px] rounded-full transition-all duration-500 transform shadow-lg shadow-med-accent/30 flex items-center justify-center gap-2 md:px-8 group animate-pulse-glow ${isDemoActive && demoPhase === 'pressing' ? 'scale-[0.98] brightness-90' : isDemoActive && demoPhase === 'result' ? 'scale-0 opacity-0 w-0 p-0 overflow-hidden' : 'active:scale-95'}`}
              >
                {isDemoActive && demoPhase === 'loading' ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <Search className={`w-6 h-6 group-hover:scale-110 transition-transform`} />
                )}
                <span className="font-medium hidden md:inline font-heading text-lg">Rastrear</span>
              </button>
            </div>

            {/* Trust Badges — proper spacing from search, won't overlap */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="mt-6 flex items-center justify-center gap-8 text-sm text-med-navy/30 font-normal">
              <span className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-emerald-500" /> Cifrado end-to-end</span>
              <span className="flex items-center gap-1.5"><Lock className="w-4 h-4 text-slate-400" /> Sin apps ni cuentas</span>
            </motion.div>
          </motion.div>

          {/* Benefits Icons — generous top margin */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.1, delayChildren: 0.6 }
              }
            }}
            className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-3xl mx-auto mt-8">
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 }
              }}
              className="flex items-center gap-4 bg-white/40 backdrop-blur-md border border-slate-200/50 p-5 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all group hover:bg-white/60 cursor-default">
              <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm border border-slate-200 group-hover:scale-110 group-hover:border-indigo-500/30 transition-all duration-300">
                <BellOff className="w-5 h-5 text-indigo-500" />
              </div>
              <p className="text-sm font-medium text-med-navy text-left leading-snug">Cero interrupciones<br />al quirófano</p>
            </motion.div>

            <motion.div
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 }
              }}
              className="flex items-center gap-4 bg-white/40 backdrop-blur-md border border-slate-200/50 p-5 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all group hover:bg-white/60 cursor-default">
              <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm border border-slate-200 group-hover:scale-110 group-hover:border-emerald-500/30 transition-all duration-300">
                <Users className="w-5 h-5 text-emerald-500" />
              </div>
              <p className="text-sm font-medium text-med-navy text-left leading-snug">Recepción<br />sin saturar</p>
            </motion.div>

            <motion.div
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 }
              }}
              className="flex items-center gap-4 bg-white/40 backdrop-blur-md border border-slate-200/50 p-5 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all group hover:bg-white/60 cursor-default">
              <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm border border-slate-200 group-hover:scale-110 group-hover:border-med-accent/30 transition-all duration-300">
                <ShieldCheck className="w-5 h-5 text-med-accent" />
              </div>
              <p className="text-sm font-medium text-med-navy text-left leading-snug">Percepción institucional<br />superior</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Curved SVG Separator */}
      <div className="relative w-full overflow-hidden leading-none bg-white rotate-180" style={{ transform: 'rotate(180deg) translateY(1px)' }}>
        <svg
          data-name="Layer 1"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          className="relative block w-full h-[80px] md:h-[150px]"
        >
          <path
            d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
            className="fill-slate-50"
          ></path>
        </svg>
      </div>

      <section id="familias" className="pt-20 pb-4 md:pb-12 bg-white relative overflow-hidden scroll-mt-24">
        {/* Decorative background gradients */}
        <div className="absolute top-1/2 right-0 w-[500px] h-[500px] bg-emerald-100/50 rounded-full blur-[100px] pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-med-light/30 rounded-full blur-[80px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

            {/* Phone Mockup Representation */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="relative flex justify-center lg:justify-start lg:pl-10 order-2 lg:order-1">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-emerald-400/20 blur-[60px] rounded-full"></div>

              <div className="w-[300px] h-[620px] bg-slate-900 rounded-[3rem] border-[6px] border-slate-800 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.3)] relative z-10 overflow-hidden flex flex-col ring-1 ring-white/10">
                {/* iPhone Notch */}
                <div className="absolute top-0 w-full flex justify-center z-30">
                  <div className="w-32 h-7 bg-slate-900 rounded-b-3xl"></div>
                </div>

                {/* App Screen */}
                <div className="flex-1 bg-[#F8FAFC] flex flex-col pt-14">
                  {/* Status Header */}
                  <div className="px-6 pb-6">
                    <p className="text-[11px] font-medium tracking-[0.08em] uppercase text-slate-400 mb-1">Seguimiento en Vivo</p>
                    <div className="text-4xl font-mono font-bold text-med-navy tracking-tight">MT001</div>
                    <div className="mt-3 text-[10px] text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-100 font-medium uppercase tracking-[0.08em] inline-flex items-center gap-1.5 transition-all duration-500">
                      <div className={`w-1.5 h-1.5 rounded-full ${activeStep < 4 ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`}></div>
                      {activeStep < 4 ? 'Actualizado hace 2 min' : 'Proceso Finalizado'}
                    </div>
                  </div>

                  {/* Timeline Mockup */}
                  <div className="flex-1 bg-white rounded-t-[2.5rem] shadow-[0_-20px_40px_rgba(0,0,0,0.03)] p-8 relative border-t border-slate-100 overflow-hidden">
                    <div className="absolute left-11 top-12 bottom-16 w-0.5 bg-slate-100"></div>

                    <div className="space-y-6 relative z-10">
                      {/* Step 0: Admisión */}
                      <div className={`flex gap-4 transition-all duration-500 ${activeStep >= 0 ? 'opacity-100' : 'opacity-40'}`}>
                        <div className={`w-7 h-7 rounded-full border-4 border-white flex items-center justify-center flex-shrink-0 z-10 relative mt-0.5 shadow-sm transition-colors duration-500 ${activeStep > 0 ? 'bg-med-accent' : activeStep === 0 ? 'bg-emerald-500' : 'bg-slate-200'}`}>
                          {activeStep === 0 && <div className="absolute inset-0 bg-emerald-500 rounded-full animate-ping opacity-60"></div>}
                        </div>
                        <div className={`transition-all duration-500 w-full ${activeStep === 0 ? 'bg-gradient-to-br from-emerald-50 to-white border border-emerald-100/60 p-4 rounded-2xl -mt-3 shadow-sm' : ''}`}>
                          <p className={`text-xs font-medium uppercase tracking-[0.06em] mb-1 ${activeStep === 0 ? 'text-emerald-700' : 'text-slate-800'}`}>Admisión</p>
                          <p className="text-[10px] text-slate-400 font-mono font-medium">08:00 AM</p>
                        </div>
                      </div>

                      {/* Step 1: Preparación */}
                      <div className={`flex gap-4 transition-all duration-500 ${activeStep >= 1 ? 'opacity-100' : 'opacity-40'}`}>
                        <div className={`w-7 h-7 rounded-full border-4 border-white flex items-center justify-center flex-shrink-0 z-10 relative mt-0.5 shadow-sm transition-colors duration-500 ${activeStep > 1 ? 'bg-med-accent' : activeStep === 1 ? 'bg-emerald-500' : 'bg-slate-200'}`}>
                          {activeStep === 1 && <div className="absolute inset-0 bg-emerald-500 rounded-full animate-ping opacity-60"></div>}
                        </div>
                        <div className={`transition-all duration-500 w-full ${activeStep === 1 ? 'bg-gradient-to-br from-emerald-50 to-white border border-emerald-100/60 p-4 rounded-2xl -mt-3 shadow-sm' : ''}`}>
                          <p className={`text-xs font-medium uppercase tracking-[0.06em] mb-1 ${activeStep === 1 ? 'text-emerald-700' : 'text-slate-800'}`}>Preparación</p>
                          {activeStep >= 1 && <p className="text-[10px] text-slate-400 font-mono font-medium">09:15 AM</p>}
                        </div>
                      </div>

                      {/* Step 2: En Cirugía */}
                      <div className={`flex gap-4 transition-all duration-500 ${activeStep >= 2 ? 'opacity-100' : 'opacity-40'}`}>
                        <div className={`w-7 h-7 rounded-full border-4 border-white flex items-center justify-center flex-shrink-0 z-10 relative mt-0.5 shadow-sm transition-colors duration-500 ${activeStep > 2 ? 'bg-med-accent' : activeStep === 2 ? 'bg-emerald-500' : 'bg-slate-200'}`}>
                          {activeStep === 2 && <div className="absolute inset-0 bg-emerald-500 rounded-full animate-ping opacity-60"></div>}
                        </div>
                        <div className={`transition-all duration-500 w-full ${activeStep === 2 ? 'bg-gradient-to-br from-emerald-50 to-white border border-emerald-100/60 p-4 rounded-2xl -mt-3 shadow-sm' : ''}`}>
                          <p className={`text-xs font-medium uppercase tracking-[0.06em] ${activeStep === 2 ? 'text-emerald-700 mb-1.5' : 'text-slate-800 mb-1'}`}>En Cirugía</p>
                          {activeStep === 2 && (
                            <div className="animate-fade-in">
                              <p className="text-[10px] text-slate-500 font-medium leading-relaxed">Procedimiento quirúrgico en curso. Todo marcha según lo planeado.</p>
                              <div className="mt-3 h-1.5 w-full bg-emerald-100 rounded-full overflow-hidden">
                                <div className="h-full bg-emerald-500 w-2/3 rounded-full relative overflow-hidden">
                                  <div className="absolute inset-0 bg-white/20 animate-[shimmer_2s_infinite]"></div>
                                </div>
                              </div>
                            </div>
                          )}
                          {activeStep > 2 && <p className="text-[10px] text-slate-400 font-mono font-medium">10:30 AM</p>}
                        </div>
                      </div>

                      {/* Step 3: Recuperación */}
                      <div className={`flex gap-4 transition-all duration-500 ${activeStep >= 3 ? 'opacity-100' : 'opacity-40'}`}>
                        <div className={`w-7 h-7 rounded-full border-4 border-white flex items-center justify-center flex-shrink-0 z-10 relative mt-0.5 shadow-sm transition-colors duration-500 ${activeStep > 3 ? 'bg-med-accent' : activeStep === 3 ? 'bg-emerald-500' : 'bg-slate-200'}`}>
                          {activeStep === 3 && <div className="absolute inset-0 bg-emerald-500 rounded-full animate-ping opacity-60"></div>}
                        </div>
                        <div className={`transition-all duration-500 w-full ${activeStep === 3 ? 'bg-gradient-to-br from-emerald-50 to-white border border-emerald-100/60 p-4 rounded-2xl -mt-3 shadow-sm' : ''}`}>
                          <p className={`text-xs font-medium uppercase tracking-[0.06em] ${activeStep === 3 ? 'text-emerald-700 mb-1.5' : 'text-slate-800 mb-1'}`}>Recuperación</p>
                          {activeStep === 3 && (
                            <div className="animate-fade-in">
                              <p className="text-[10px] text-slate-500 font-medium leading-relaxed">Cirugía concluida con éxito. Paciente en recuperación.</p>
                            </div>
                          )}
                          {activeStep > 3 && <p className="text-[10px] text-slate-400 font-mono font-medium">01:45 PM</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Value Prop Text */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: { staggerChildren: 0.15 }
                }
              }}
              className="order-1 lg:order-2">
              <motion.div variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 mb-6 transition-colors">
                <HeartPulse className="w-4 h-4 text-emerald-600" />
                <span className="text-[11px] font-medium text-emerald-800 uppercase tracking-[0.08em] font-heading">Experiencia Familiar</span>
              </motion.div>
              <motion.h2 variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }} className="text-3xl md:text-5xl font-bold text-med-navy font-heading mb-6 tracking-[-0.025em] leading-[1.15]">
                Información en tiempo real. <span className="text-emerald-500">Tranquilidad inmediata.</span>
              </motion.h2>
              <motion.p variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }} className="text-lg text-med-navy/50 mb-10 leading-[1.6] font-normal">
                Cada etapa del procedimiento se refleja al instante en el celular de la familia. Sin apps, sin cuentas, sin preguntas en recepción. Solo un código y toda la certeza.
              </motion.p>

              <ul className="space-y-8">
                <motion.li variants={{ hidden: { opacity: 0, x: 20 }, visible: { opacity: 1, x: 0 } }} className="flex items-start gap-4 group cursor-default">
                  <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center flex-shrink-0 mt-1 shadow-sm border border-slate-200 group-hover:scale-110 group-hover:border-med-accent/30 transition-all duration-300">
                    <Clock className="w-6 h-6 text-med-accent" />
                  </div>
                  <div>
                    <h4 className="text-xl font-medium text-med-navy mb-2 font-heading tracking-[-0.015em]">Privacidad por diseño</h4>
                    <p className="text-base text-med-navy/50 leading-[1.6] font-normal">Los datos del paciente se eliminan automáticamente 3 horas después de la cirugía. Sin historial, sin rastro. Cumplimiento clínico desde la arquitectura.</p>
                  </div>
                </motion.li>
                <motion.li variants={{ hidden: { opacity: 0, x: 20 }, visible: { opacity: 1, x: 0 } }} className="flex items-start gap-4 group cursor-default">
                  <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center flex-shrink-0 mt-1 shadow-sm border border-slate-200 group-hover:scale-110 group-hover:border-emerald-500/30 transition-all duration-300">
                    <Zap className="w-6 h-6 text-emerald-500" />
                  </div>
                  <div>
                    <h4 className="text-xl font-medium text-med-navy mb-2 font-heading tracking-[-0.015em]">Cada cambio de estatus, al instante</h4>
                    <p className="text-base text-med-navy/50 leading-[1.6] font-normal">El familiar sabe que el paciente pasó a recuperación en el mismo segundo que el equipo lo registra. Sin intermediarios, sin demoras.</p>
                  </div>
                </motion.li>
                <motion.li variants={{ hidden: { opacity: 0, x: 20 }, visible: { opacity: 1, x: 0 } }} className="flex items-start gap-4 group cursor-default">
                  <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center flex-shrink-0 mt-1 shadow-sm border border-slate-200 group-hover:scale-110 group-hover:border-slate-800/30 transition-all duration-300">
                    <Shield className="w-6 h-6 text-slate-700" />
                  </div>
                  <div>
                    <h4 className="text-xl font-medium text-med-navy mb-2 font-heading tracking-[-0.015em]">Un código. Nada más.</h4>
                    <p className="text-base text-med-navy/50 leading-[1.6] font-normal">Sin descargas, sin registros, sin datos personales. El familiar recibe un código impreso en admisión y accede desde cualquier navegador.</p>
                  </div>
                </motion.li>
              </ul>
            </motion.div>

          </div>
        </div>
      </section>

      {/* 4 Pilares de Valor — Isometric Features */}
      <FeaturesIsometric />

      {/* Hospital/B2B Section */}
      <section id="hospitales" className="py-32 bg-slate-900 relative overflow-hidden scroll-mt-24">
        {/* Background elements */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-med-accent/10 blur-3xl rounded-none"></div>
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-indigo-500/10 blur-3xl rounded-none"></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

            {/* Value Prop Text */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: { staggerChildren: 0.15 }
                }
              }}
            >
              <motion.div variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 mb-6 backdrop-blur-md">
                <Building className="w-4 h-4 text-med-accent" />
                <span className="text-[11px] font-medium text-white/80 uppercase tracking-[0.08em] font-heading">Solución Hospitalaria</span>
              </motion.div>
              <motion.h2 variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }} className="text-3xl md:text-5xl font-bold text-white font-heading mb-6 tracking-[-0.025em] leading-[1.15]">
                Tecnología que eleva la percepción de tu hospital.
              </motion.h2>
              <motion.p variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }} className="text-lg text-white/50 mb-8 leading-[1.6] font-normal">
                Los mejores hospitales no solo operan bien — se sienten bien. Hospital Status digitaliza la experiencia en sala de espera y convierte un punto de fricción en un diferenciador competitivo.
              </motion.p>

              <ul className="space-y-6 mb-10">
                <motion.li variants={{ hidden: { opacity: 0, x: 20 }, visible: { opacity: 1, x: 0 } }} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-1 shadow-inner border border-white/5">
                    <HeartPulse className="w-5 h-5 text-med-accent" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-white mb-1">Experiencia centrada en el paciente</h4>
                    <p className="text-base text-white/50 leading-[1.6] font-normal">Información en tiempo real que reduce la ansiedad familiar y fortalece la percepción de atención integral.</p>
                  </div>
                </motion.li>
                <motion.li variants={{ hidden: { opacity: 0, x: 20 }, visible: { opacity: 1, x: 0 } }} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-1 shadow-inner border border-white/5">
                    <Activity className="w-5 h-5 text-med-accent" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-white mb-1">Operación sin interrupciones</h4>
                    <p className="text-base text-white/50 leading-[1.6] font-normal">Las familias dejan de saturar recepción. El estatus fluye directo desde el quirófano sin que nadie interrumpa al equipo médico.</p>
                  </div>
                </motion.li>
                <motion.li variants={{ hidden: { opacity: 0, x: 20 }, visible: { opacity: 1, x: 0 } }} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-1 shadow-inner border border-white/5">
                    <Zap className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-white mb-1">La sala de espera como canal de revenue</h4>
                    <p className="text-base text-white/50 leading-[1.6] font-normal">Espacios publicitarios programables en las pantallas. Promociona servicios propios — cafetería, check-ups, membresías — o genera ingresos con anunciantes externos. <span className="text-emerald-400 font-semibold">El sistema se paga solo.</span></p>
                  </div>
                </motion.li>
              </ul>

              <motion.button onClick={() => navigate('/contacto')} variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }} className="inline-flex items-center justify-center gap-2 bg-white text-med-navy px-8 py-4 rounded-full font-medium hover:bg-slate-100 transition-colors shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_30px_rgba(255,255,255,0.5)]">
                Agendar una demo <ArrowRight className="w-5 h-5" />
              </motion.button>
            </motion.div>

            {/* TV Mockup Representation */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="relative lg:ml-8"
            >
              {/* Glow behind TV */}
              <div className="absolute inset-0 bg-med-accent/30 blur-[100px] rounded-full"></div>

              <div className="aspect-[16/9] bg-slate-800 rounded-2xl border-4 border-slate-700 shadow-2xl overflow-hidden relative group z-10">
                {/* Mockup Top */}
                <div className="absolute top-0 w-full h-10 bg-slate-900 border-b border-white/10 flex items-center px-4 justify-between">
                  <div className="flex items-center gap-2">
                    <Building className="w-3.5 h-3.5 text-white/70" />
                    <span className="text-white/70 font-medium text-[10px] tracking-[0.06em] uppercase">Recepción Digital</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></div>
                    <span className="text-[9px] uppercase text-white/70 font-medium tracking-[0.08em]">EN VIVO</span>
                  </div>
                </div>

                {/* Mockup Rows (Surgical Board) */}
                <div className="absolute top-10 bottom-10 w-full bg-slate-800/80 p-3 space-y-2 backdrop-blur-sm">
                  {/* Header Row */}
                  <div className="flex justify-between px-2 text-[8px] text-slate-400 font-medium tracking-[0.08em] uppercase mb-2">
                    <span>Paciente</span>
                    <span>Estatus</span>
                  </div>
                  {/* Data Rows */}
                  <div className="w-full h-8 bg-slate-700/40 rounded flex items-center px-3 justify-between border border-white/5 airline-row">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-mono text-xs font-bold">MT001</span>
                    </div>
                    <div
                      className={`px-2 py-0.5 rounded border text-[9px] font-bold transition-all duration-700 ${tvPhase === 0
                        ? 'bg-med-accent/20 text-med-accent border-med-accent/30'
                        : 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30'
                        }`}
                    >
                      {tvPhase === 0 ? 'EN CIRUGÍA' : 'RECUPERACIÓN'}
                    </div>
                  </div>
                  <div className="w-full h-8 bg-slate-700/40 rounded flex items-center px-3 justify-between border border-white/5 airline-row">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-mono text-xs font-bold">ARN88</span>
                    </div>
                    <div
                      className={`px-2 py-0.5 rounded border text-[9px] font-bold transition-all duration-700 ${tvPhase === 0
                        ? 'bg-amber-500/20 text-amber-500 border-amber-500/30'
                        : 'bg-med-accent/20 text-med-accent border-med-accent/30'
                        }`}
                    >
                      {tvPhase === 0 ? 'ANESTESIA' : 'EN CIRUGÍA'}
                    </div>
                  </div>
                  <div className="w-full h-8 bg-slate-700/40 rounded flex items-center px-3 justify-between border border-white/5 airline-row">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-mono text-xs font-bold">LMX12</span>
                    </div>
                    <div
                      className={`px-2 py-0.5 rounded border text-[9px] font-bold transition-all duration-700 ${tvPhase === 0
                        ? 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30'
                        : 'bg-slate-700/40 text-slate-400 border-white/10'
                        }`}
                    >
                      {tvPhase === 0 ? 'RECUPERACIÓN' : 'SALIENDO'}
                    </div>
                  </div>
                </div>

                {/* Mockup Ad Banner (Digital Signage Feature) */}
                <div className="absolute bottom-0 w-full h-10 bg-gradient-to-r from-med-navy to-indigo-900 border-t border-white/10 flex items-center px-4 justify-between overflow-hidden">
                  <div className="flex items-center gap-2.5 relative z-10 w-full">
                    <div className="w-5 h-5 rounded-md bg-white/10 flex items-center justify-center flex-shrink-0 border border-white/20">
                      <span className="text-[10px]">☕️</span>
                    </div>
                    <p className="text-white text-[10px] font-medium tracking-wide truncate">
                      ¿Larga espera? <span className="font-bold">Conoce nuestras sincronizadas en la cafetería</span> del hospital. Abierto 24 hrs.
                    </p>
                  </div>
                  {/* Subtle shimmer effect on the banner */}
                  <div className="absolute top-0 left-[-100%] w-1/2 h-full bg-gradient-to-r from-transparent via-white/10 to-transparent group-hover:left-[200%] transition-all duration-[2s] ease-in-out"></div>
                </div>
              </div>

              {/* Stand for the TV */}
              <div className="static mt-[-2px] flex flex-col items-center relative z-0">
                <div className="w-16 h-8 bg-gradient-to-b from-slate-700 to-slate-800 rounded-b-lg border-x border-b border-white/10"></div>
                <div className="w-32 h-2 bg-slate-600 rounded-full mt-1 shadow-lg"></div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Developer / Integration Section — Terminal */}
      <FeatureTerminal />

      {/* CTA / Footer */}
      <footer className="bg-med-navy py-12 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="text-white opacity-80 text-sm flex flex-col items-center md:items-start gap-1">
            <span>© 2026 Hospital Status Health Systems.</span>
            <span className="text-[10px] font-medium tracking-[0.08em] uppercase text-white/30">Desarrollado por ⚕️ ViMaware 2026</span>
          </div>
          <Link to="/staff" className="text-white/50 hover:text-white text-sm font-medium flex items-center gap-2 transition-colors">
            Portal para hospitales <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </footer>

    </div>
  );
};

const App: React.FC = () => {
  // ── Capa 1: Auto-actualización agresiva ──────────────────────────────────
  // Verifica /version.json cada 2 min y al volver a la app/tab.
  // Si detecta un deploy nuevo → recarga automática sin intervención manual.
  useVersionPoller();

  // Global Auth Enforcement (Zero Trust Listener)
  useEffect(() => {
    const handleAuthExpired = () => {
      console.log("[Zero-Trust] Se detectó 403. Purgando sesión compartida de Operativo.");
      
      // UI Alerta de Inactividad
      alert("Por tu seguridad, tu sesión ha expirado por inactividad. Por favor, inicia sesión nuevamente.");
      
      storageService.logout();
      
      const currentPath = window.location.pathname;
      console.log("[Zero-Trust] Ruta actual al momento del bloqueo:", currentPath);

      const isPublic = ['/tv', '/family', '/login'].some(p => currentPath.toLowerCase().startsWith(p));
      if (!isPublic) {
        if (currentPath.toLowerCase().includes('hq') || currentPath.toLowerCase().includes('/c/') || currentPath.toLowerCase().includes('admin')) {
          console.log("[ZERO TRUST] Activando expulsion a Búnker Edge (CF Access HQ)");
          if (currentPath.replace(/\/$/, '') !== '/hq') {
            window.location.href = '/hq';
          }
        } else {
          console.log("[ZERO TRUST] Activando expulsion a Staff Login");
          window.location.href = '/login';
        }
      } else {
        console.log("[ZERO TRUST] Ruta es pública, omitiendo redirección.");
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const currentPath = window.location.pathname;
        const isProtected = currentPath.includes('/staff') || currentPath.includes('/admin') || currentPath.includes('/hq');
        
        // Zero Trust: Al despertar la pantalla, verificamos la persistencia local
        if (isProtected && !storageService.isAuthenticated()) {
            console.log("[Zero-Trust] Token ausente al destapar navegador. Activando purgatorio.");
            handleAuthExpired();
        }
      }
    };

    window.addEventListener('auth:expired', handleAuthExpired);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Active Heartbeat for Zombie Sessions (Pestañas web abiertas eternamente)
    const zombieSessionInterval = setInterval(() => {
      const currentPath = window.location.pathname;
      const isProtected = currentPath.includes('/staff') || currentPath.includes('/admin') || currentPath.includes('/hq');
      if (isProtected && !storageService.isAuthenticated()) {
         console.log("[Zero-Trust] Heartbeat detectó sesión expirada. Auto-destrucción iniciada.");
         handleAuthExpired();
      }
    }, 60000); // Check every 60 seconds

    return () => {
      window.removeEventListener('auth:expired', handleAuthExpired);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(zombieSessionInterval);
    };
  }, []);

  return (
    <Router>
      <SupervisionBanner />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/family" element={<FamilyView />} />
        <Route path="/login" element={<Login />} />
        <Route path="/logout" element={<LogoutSuccess />} />
        <Route
          path="/staff"
          element={
            <ProtectedRoute>
              <HospitalRoleSelection />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/dashboard"
          element={
            <ProtectedRoute>
              <StaffDashboard />
            </ProtectedRoute>
          }
        />
        {/* Backward compat: /staff/analytics → /staff/paneladmin/analytics */}
        <Route path="/staff/analytics" element={<Navigate to="/staff/paneladmin/analytics" replace />} />
        {/* Backward compat: /staff/publicidad → /staff/paneladmin/publicidad */}
        <Route path="/staff/publicidad" element={<Navigate to="/staff/paneladmin/publicidad" replace />} />
        {/* Admin Panel Hub + Sub-routes */}
        <Route
          path="/staff/paneladmin"
          element={
            <ProtectedRoute>
              <AdminHub />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/paneladmin/:hospitalId"
          element={
            <ProtectedRoute>
              <AdminHub />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/paneladmin/analytics"
          element={
            <ProtectedRoute>
              <HospitalDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/paneladmin/analytics/:hospitalId"
          element={
            <ProtectedRoute>
              <HospitalDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/paneladmin/publicidad"
          element={
            <ProtectedRoute>
              <AdminPublicidad />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/paneladmin/publicidad/:hospitalId"
          element={
            <ProtectedRoute>
              <AdminPublicidad />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/paneladmin/accesos"
          element={
            <ProtectedRoute>
              <AdminAccesos />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/paneladmin/accesos/:hospitalId"
          element={
            <ProtectedRoute>
              <AdminAccesos />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/paneladmin/integraciones"
          element={
            <ProtectedRoute>
              <AdminIntegraciones />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/paneladmin/integraciones/:hospitalId"
          element={
            <ProtectedRoute>
              <AdminIntegraciones />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/room/:roomId"
          element={
            <ProtectedRoute>
              <StaffRoomControl />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/patient/:patientId"
          element={
            <ProtectedRoute>
              <StaffRoomControl />
            </ProtectedRoute>
          }
        />
        {/* Admin Routes — Protegido por Cloudflare Zero Trust (no necesita PIN) */}
        <Route path="/admin" element={<Navigate to="/hq" replace />} />
        <Route path="/admin/login" element={<Navigate to="/hq" replace />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />

        {/* Nuevo Portal HQ - Cloudflare Access */}
        <Route path="/hq" element={<AdminHQLogin />} />
        <Route path="/HQ" element={<Navigate to="/hq" replace />} />
        <Route path="/corporate" element={<Navigate to="/hq" replace />} />

        {/* Backward compat: old hospital-admin → new accesos */}
        <Route path="/hospital-admin" element={<Navigate to="/staff/paneladmin/accesos" replace />} />

        {/* Developer Portal */}
        <Route path="/docs" element={<DevPortal />} />
        <Route path="/docs/:token" element={<DevPortal />} />

        {/* Contacto */}
        <Route path="/contacto" element={<Contacto />} />

        {/* Dynamic Hospital Routes - MUST BE LAST */}
        <Route path="/c/:handle" element={<Navigate to="/c/:handle/dashboard" replace />} />
        <Route path="/c/:handle/dashboard" element={<GroupAdminDashboard />} />
        <Route path="/:handle" element={<HospitalHome />} />
        <Route path="/:handle/staff" element={<HospitalStaffLogin />} />
        <Route path="/:handle/board" element={<PublicBoard />} />
      </Routes>
    </Router>
  );
};


export default App;